import { Capacitor } from "@capacitor/core";
import { initializeApp } from "firebase/app";
import { getAuth, initializeAuth, indexedDBLocalPersistence } from "firebase/auth";
import { initializeFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";

const authDomain = typeof window !== 'undefined' && ['ridaz.app', 'www.ridaz.app'].includes(window.location.hostname)
  ? window.location.hostname
  : "ridaz-4ecb9.firebaseapp.com";

// Pulled from env vars rather than hardcoded so this file can be shared/
// screenshotted without handing out the project's live config directly, and
// so the key can be rotated without a code change. Firebase's web apiKey
// isn't a secret in the traditional sense - Google's own docs say it's safe
// to ship client-side, since access control is enforced by Firestore/Storage
// Security Rules, not by hiding this value - but keeping it out of source
// is still better hygiene and costs nothing.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

// On native platforms (Capacitor iOS/Android), the default getAuth() persistence
// auto-detection does not reliably persist the session across a full app restart
// inside WKWebView — every fresh Xcode launch comes up signed out, which is why
// data saved under the real user never "shows" (the app silently falls back to
// guest mode). Forcing indexedDBLocalPersistence is the fix documented by
// @capacitor-firebase/authentication for keeping the JS SDK session in sync with
// the native (Keychain-backed) session across app relaunches.
export const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app);

// Force HTTP long-polling for ALL Firestore operations.
// WKWebView (Capacitor iOS) silently drops WebSocket connections — without this,
// even a simple getDoc() hangs and the entire app data load never starts.
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
});

export const storage = getStorage(app);

// us-central1 matches the region Cloud Functions deploy to (Firebase's
// default for 1st-gen functions) - see functions/index.js.
export const functions = getFunctions(app, 'us-central1');

export default app;
